import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  IconButton,
  Chip,
  Alert,
  Paper,
  Divider,
  Container,
  Tooltip,
  CircularProgress,
  Checkbox,
  ListItemText,
  Collapse,
  InputAdornment,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import WorkHistoryOutlinedIcon from "@mui/icons-material/WorkHistoryOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import DownloadIcon from "@mui/icons-material/Download";
import ClearIcon from "@mui/icons-material/Clear";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import api from "../utils/axiosConfig";
import { useCurrentUser } from "../utils/useCurrentUser";

const defaultEmployment = () => ({
  id: Date.now(),
  company_name: "",
  job_title: "",
  employment_type: "IT",
  start_date: "",
  end_date: "",
  technologies: [],
  others: "",
  technology_details: "",
  ref_person: "",
  relationship: "",
  contact_number: "",
  email: "",
  company_address: "",
  is_current: false,
});

const defaultSkill = () => ({
  id: Date.now(),
  technology: "",
  exp_years: "",
  exp_months: "",
  level: "Intermediate",
  technology_details: "",
});

// ── Helpers for Skill Matrix tab ──────────────────────────────────────────
const LEVEL_COLORS = {
  Beginner: { bg: "#fff3e0", text: "#e65100" },
  Intermediate: { bg: "#e3f2fd", text: "#1565c0" },
  Expert: { bg: "#e8f5e9", text: "#2e7d32" },
};
const levelStyle = (level = "") =>
  LEVEL_COLORS[level] || { bg: "#f3e5f5", text: "#6a1b9a" };

function fmtExp(years, months) {
  const y = parseInt(years) || 0;
  const m = parseInt(months) || 0;
  if (!y && !m) return "—";
  return [y > 0 ? `${y}y` : "", m > 0 ? `${m}m` : ""].filter(Boolean).join(" ");
}

function exportCSV(rows) {
  const headers = [
    "Employee ID",
    "Employee Name",
    "Technology",
    "Experience",
    "Level",
  ];
  const lines = [headers.join(",")];
  rows.forEach((emp) => {
    const skills = emp.matched_skills || emp.skills || [];
    skills.forEach((s) => {
      lines.push(
        [
          emp.emp_id,
          `"${emp.emp_name}"`,
          `"${s.technology}"`,
          fmtExp(s.exp_years, s.exp_months),
          s.level,
        ].join(","),
      );
    });
  });
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "skill_matrix.csv";
  a.click();
  URL.revokeObjectURL(url);
}

const StatCard = ({ icon, label, value, color = "#1565c0" }) => (
  <Box
    sx={{
      border: "1px solid #d0e4f7",
      borderRadius: 2,
      px: 2.5,
      py: 1.5,
      minWidth: 140,
      backgroundColor: "#f0f7ff",
      display: "flex",
      alignItems: "center",
      gap: 1.5,
    }}
  >
    <Box sx={{ color, display: "flex" }}>{icon}</Box>
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={700} color={color}>
        {value ?? "—"}
      </Typography>
    </Box>
  </Box>
);

// ── Original employee form helpers ────────────────────────────────────────

function calcDuration(start, end) {
  if (!start) return "";
  const s = new Date(start);
  const e = end ? new Date(end) : new Date();
  let months =
    (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
  if (months < 0) return "";
  const yrs = Math.floor(months / 12);
  const mos = months % 12;
  return (
    `${yrs > 0 ? yrs + " Year" + (yrs > 1 ? "s" : "") : ""}${yrs > 0 && mos > 0 ? " " : ""}${mos > 0 ? mos + " Month" + (mos > 1 ? "s" : "") : ""}` ||
    "< 1 Month"
  );
}

function calcTotalExp(employments) {
  let totalMonths = 0;
  employments.forEach((e) => {
    if (!e.start_date) return;
    const s = new Date(e.start_date);
    const end = e.end_date ? new Date(e.end_date) : new Date();
    const m =
      (end.getFullYear() - s.getFullYear()) * 12 +
      (end.getMonth() - s.getMonth());
    if (m > 0) totalMonths += m;
  });
  const yrs = Math.floor(totalMonths / 12);
  const mos = totalMonths % 12;
  return `${yrs} Year${yrs !== 1 ? "s" : ""} ${mos} Month${mos !== 1 ? "s" : ""}`;
}

// ── Format month value to MMM YYYY display ────────────────────────────────
function formatMonthDisplay(val) {
  if (!val) return "";
  const [year, month] = val.split("-");
  if (!year || !month) return val;
  const date = new Date(parseInt(year), parseInt(month) - 1, 1);
  return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

// ── Total experience in months ─────────────────────────────────────────────
function calcTotalExpMonths(employments) {
  let totalMonths = 0;
  employments.forEach((e) => {
    if (!e.start_date) return;
    const s = new Date(e.start_date);
    const end = e.end_date ? new Date(e.end_date) : new Date();
    const m =
      (end.getFullYear() - s.getFullYear()) * 12 +
      (end.getMonth() - s.getMonth());
    if (m > 0) totalMonths += m;
  });
  return totalMonths;
}
const SectionCard = ({ children, sx = {} }) => (
  <Paper
    elevation={0}
    sx={{
      border: "1px solid #e0e8f0",
      borderRadius: 2,
      p: 2.5,
      mb: 2.5,
      backgroundColor: "#fff",
      ...sx,
    }}
  >
    {children}
  </Paper>
);

const SectionTitle = ({ children, action }) => (
  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
    <Typography
      variant="subtitle1"
      fontWeight={700}
      color="#1a3a5c"
      sx={{ fontSize: 15 }}
    >
      {children}
    </Typography>
    {action}
  </Box>
);

const StatChip = ({ label, value, color = "#1565c0" }) => (
  <Box
    sx={{
      border: "1px solid #d0e4f7",
      borderRadius: 2,
      px: 2,
      py: 1,
      minWidth: 130,
      backgroundColor: "#f0f7ff",
    }}
  >
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={700} color={color}>
      {value || "—"}
    </Typography>
  </Box>
);

// ── Map DB row → frontend employment object ────────────────────────────────
function rowToEmployment(row) {
  return {
    id: row.id,
    company_name: row.company_name || "",
    job_title: row.job_title || "",
    employment_type: row.employment_type || "IT",
    start_date: row.start_date ? row.start_date.slice(0, 7) : "",
    end_date: row.end_date ? row.end_date.slice(0, 7) : "",
    technologies: Array.isArray(row.technologies_used)
      ? row.technologies_used
      : [],
    others: row.reference_id_1 || "",
    technology_details: row.reference_id_2 || "",
    ref_person: row.ref_person_name || "",
    relationship: row.ref_relationship || "",
    contact_number: row.ref_contact_number || "",
    email: row.ref_email || "",
    company_address: row.company_address || "",
    is_current: row.is_current === "Y" || row.is_current === true,
  };
}

// ── Map DB row → frontend skill object ────────────────────────────────────
function rowToSkill(row) {
  return {
    id: row.id,
    technology: row.technology || "",
    exp_years: row.experience_years != null ? String(row.experience_years) : "",
    exp_months:
      row.experience_months != null ? String(row.experience_months) : "",
    level: row.skill_level || "Intermediate",
    technology_details: row.reference_id_1 || "",
  };
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function ExperienceSkills() {
  const { roles, loading: userLoading } = useCurrentUser();

  // ── Role check ───────────────────────────────────────────────────────────
  const normalizedRoles = Array.isArray(roles)
    ? roles.map((r) =>
        typeof r === "object"
          ? (r.role_name || r.name || "").toLowerCase()
          : String(r).toLowerCase(),
      )
    : [String(roles || "").toLowerCase()];
  const isHR =
    !userLoading &&
    ["admin", "admins", "hr", "hrs", "human resource", "human resources"].some(
      (r) => normalizedRoles.includes(r),
    );

  // ── Tab state (0 = My Experience, 1 = Search Employees [HR only]) ────────
  const [activeTab, setActiveTab] = useState(0);

  // ── My Experience state ──────────────────────────────────────────────────
  const [employments, setEmployments] = useState([defaultEmployment()]);
  const [skills, setSkills] = useState([defaultSkill()]);
  const [alert, setAlert] = useState({ type: "", msg: "" });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [techOptions, setTechOptions] = useState([]);
  const [nonItOptions, setNonItOptions] = useState([]);
  const [levelOptions, setLevelOptions] = useState([]);
  const [empTypeOptions, setEmpTypeOptions] = useState([]);
  const [allowedEmailDomains, setAllowedEmailDomains] = useState([]);
  const [errors, setErrors] = useState({});
  const [originalData, setOriginalData] = useState({
    employments: [],
    skills: [],
  });

  // ── Skill Matrix (tab 2) state ───────────────────────────────────────────
  const [smSelectedTechs, setSmSelectedTechs] = useState([]);
  const [smSelectedLevel, setSmSelectedLevel] = useState("");
  const [smMinYears, setSmMinYears] = useState("");
  const [smMatchMode, setSmMatchMode] = useState("any");
  const [smNameSearch, setSmNameSearch] = useState("");
  const [smResults, setSmResults] = useState([]);
  const [smAllData, setSmAllData] = useState([]);
  const [smLoading, setSmLoading] = useState(false);
  const [smAllLoading, setSmAllLoading] = useState(false);
  const [smSearched, setSmSearched] = useState(false);
  const [smExpandedRow, setSmExpandedRow] = useState(null);
  const [smError, setSmError] = useState("");

  // ── Load existing data on mount ──────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          workRes,
          skillsRes,
          techRes,
          nonItRes,
          levelRes,
          empTypeRes,
          emailDomainRes,
        ] = await Promise.all([
          api.get("/api/employee-experience/work"),
          api.get("/api/employee-experience/skills"),
          api.get("/commonVariables/tech-options"),
          api.get("/commonVariables/non-it-options"),
          api.get("/commonVariables/skill-levels"),
          api.get("/commonVariables/employment-types"),
          api.get("/commonVariables/email-domains"),
        ]);
        const workRows = workRes.data;
        const skillRows = skillsRes.data;
        if (workRows.length > 0) setEmployments(workRows.map(rowToEmployment));
        else setEmployments([]);
        if (skillRows.length > 0) setSkills(skillRows.map(rowToSkill));
        else setSkills([]);
        const empSnap =
          workRows.length > 0 ? workRows.map(rowToEmployment) : [];
        const skillSnap = skillRows.length > 0 ? skillRows.map(rowToSkill) : [];
        setOriginalData({ employments: empSnap, skills: skillSnap });
        if (techRes.data.length)
          setTechOptions(techRes.data.map((r) => r.label));
        if (nonItRes.data.length)
          setNonItOptions(nonItRes.data.map((r) => r.label));
        if (levelRes.data.length)
          setLevelOptions(levelRes.data.map((r) => r.label));
        if (empTypeRes.data.length)
          setEmpTypeOptions(empTypeRes.data.map((r) => r.label));
        if (emailDomainRes.data.length)
          setAllowedEmailDomains(
            emailDomainRes.data.map((r) => r.domain.toLowerCase()),
          );
      } catch (err) {
        setAlert({ type: "error", msg: "Failed to load existing data." });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // ── Load all-employee data when HR opens the Search tab ─────────────────
  useEffect(() => {
    if (!isHR || activeTab !== 1 || smAllData.length > 0) return;
    const fetchAll = async () => {
      setSmAllLoading(true);
      try {
        const res = await api.get("/api/employee-experience/all");
        setSmAllData(res.data || []);
      } catch (err) {
        setSmError("Failed to load employee data.");
      } finally {
        setSmAllLoading(false);
      }
    };
    fetchAll();
  }, [isHR, activeTab, smAllData.length]);

  // ── Skill search ─────────────────────────────────────────────────────────
  const handleSkillSearch = useCallback(async () => {
    setSmLoading(true);
    setSmError("");
    setSmSearched(true);
    try {
      const params = new URLSearchParams();
      if (smSelectedTechs.length > 0)
        params.set("technologies", smSelectedTechs.join(","));
      if (smSelectedLevel) params.set("level", smSelectedLevel);
      if (smMinYears) params.set("min_years", smMinYears);
      params.set("match", smMatchMode);
      const res = await api.get(
        `/api/employee-experience/skill-search?${params}`,
      );
      setSmResults(res.data || []);
    } catch (err) {
      setSmError(err.response?.data?.message || "Search failed.");
    } finally {
      setSmLoading(false);
    }
  }, [smSelectedTechs, smSelectedLevel, smMinYears, smMatchMode]);

  const handleSmClear = () => {
    setSmSelectedTechs([]);
    setSmSelectedLevel("");
    setSmMinYears("");
    setSmMatchMode("any");
    setSmNameSearch("");
    setSmResults([]);
    setSmSearched(false);
    setSmError("");
  };

  const totalExp = calcTotalExp(employments);
  const itExp = calcTotalExp(
    employments.filter((e) => e.employment_type === "IT"),
  );
  const nonItExp = calcTotalExp(
    employments.filter((e) => e.employment_type === "Non-IT"),
  );
  const currentEmp = employments.find((e) => e.is_current);
  const hasExistingData =
    originalData.employments.length > 0 || originalData.skills.length > 0;
  const hasChanges =
    JSON.stringify({ employments, skills }) !==
    JSON.stringify({
      employments: originalData.employments,
      skills: originalData.skills,
    });

  // ── Employment handlers ──────────────────────────────────────────────────
  const addEmployment = () =>
    setEmployments((prev) => [...prev, defaultEmployment()]);

  const removeEmployment = (id) =>
    setEmployments((prev) => prev.filter((e) => e.id !== id));

  const updateEmp = (id, field, value) => {
    setErrors((prev) => {
      const e = { ...prev };
      delete e[`emp_${id}_${field}`];
      return e;
    });
    setEmployments((prev) =>
      prev.map((e) => (e.id === id ? { ...e, [field]: value } : e)),
    );
  };

  const addTech = (id, tech) => {
    if (!tech) return;
    setEmployments((prev) =>
      prev.map((e) =>
        e.id === id && !e.technologies.includes(tech)
          ? { ...e, technologies: [...e.technologies, tech] }
          : e,
      ),
    );
  };

  const removeTech = (id, tech) =>
    setEmployments((prev) =>
      prev.map((e) =>
        e.id === id
          ? { ...e, technologies: e.technologies.filter((t) => t !== tech) }
          : e,
      ),
    );

  // ── Skill handlers ───────────────────────────────────────────────────────
  const addSkill = () => setSkills((prev) => [...prev, defaultSkill()]);

  const removeSkill = (id) =>
    setSkills((prev) => prev.filter((s) => s.id !== id));

  const updateSkill = (id, field, value) => {
    setErrors((prev) => {
      const e = { ...prev };
      delete e[`skill_${id}_${field}`];
      delete e[`skill_${id}_exp`];
      return e;
    });
    setSkills((prev) =>
      prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)),
    );
  };

  const skillTotal = (s) => {
    const y = parseInt(s.exp_years) || 0;
    const m = parseInt(s.exp_months) || 0;
    if (!y && !m) return "—";
    return `${y > 0 ? y + " Yr" + (y > 1 ? "s" : "") : ""}${y > 0 && m > 0 ? " " : ""}${m > 0 ? m + " Mo" : ""}`;
  };

  // ── Validation ───────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};

    employments.forEach((emp) => {
      const prefix = `emp_${emp.id}`;

      if (!emp.company_name.trim())
        errs[`${prefix}_company_name`] = "Company Name is required";

      if (!emp.job_title.trim())
        errs[`${prefix}_job_title`] = "Job Title is required";

      if (!emp.employment_type)
        errs[`${prefix}_employment_type`] = "Employment Type is required";

      if (!emp.start_date)
        errs[`${prefix}_start_date`] = "Start Date is required";

      if (!emp.is_current && !emp.end_date)
        errs[`${prefix}_end_date`] = "End Date is required";

      if (emp.start_date && emp.end_date && !emp.is_current) {
        if (new Date(emp.end_date) <= new Date(emp.start_date))
          errs[`${prefix}_end_date`] = "End Date must be after Start Date";
      }

      // Fix 5: warn when a past-dated job (end date already set) is toggled to is_current
      if (emp.is_current && emp.start_date) {
        const startYear = parseInt(emp.start_date.slice(0, 4));
        const currentYear = new Date().getFullYear();
        if (startYear < currentYear - 30) {
          errs[`${prefix}_start_date`] =
            "Start date seems too old for a current position. Please verify.";
        }
      }

      if (!emp.technologies || emp.technologies.length === 0)
        errs[`${prefix}_technologies`] = "At least one Technology is required";

      if (emp.contact_number) {
        if (!/^\d{10}$/.test(emp.contact_number))
          errs[`${prefix}_contact_number`] =
            "Contact Number must be exactly 10 digits (no special characters)";
      }

      if (emp.email) {
        const emailTrimmed = emp.email.trim();
        // Check for spaces in email
        if (/\s/.test(emp.email)) {
          errs[`${prefix}_email`] = "Email must not contain spaces";
        } else if (
          !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(emailTrimmed)
        ) {
          errs[`${prefix}_email`] =
            "Invalid email format (e.g. name@domain.com)";
        }
      }
    });

    // Fix 4: detect duplicate technologies in the skills table
    const seenTechs = {};
    skills.forEach((skill) => {
      if (skill.technology) {
        if (seenTechs[skill.technology]) {
          // flag the duplicate entry (current one)
          errs[`skill_${skill.id}_technology`] =
            `"${skill.technology}" is already added. Please remove the duplicate.`;
        } else {
          seenTechs[skill.technology] = true;
        }
      }
    });

    skills.forEach((skill) => {
      const prefix = `skill_${skill.id}`;

      if (!skill.technology)
        errs[`${prefix}_technology`] = "Technology is required";

      const yrs = parseInt(skill.exp_years) || 0;
      const mos = parseInt(skill.exp_months) || 0;
      if (yrs === 0 && mos === 0)
        errs[`${prefix}_exp`] = "Experience (years or months) is required";

      // Skill exp must not exceed total employment experience
      const skillTotalMonths = yrs * 12 + mos;
      const totalEmpMonths = calcTotalExpMonths(employments);
      if (totalEmpMonths > 0 && skillTotalMonths > totalEmpMonths) {
        const tYrs = Math.floor(totalEmpMonths / 12);
        const tMos = totalEmpMonths % 12;
        errs[`${prefix}_exp`] =
          `Skill experience cannot exceed total work experience (${tYrs > 0 ? tYrs + "y " : ""}${tMos > 0 ? tMos + "m" : ""})`;
      }
    });

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!validate()) {
      setAlert({
        type: "error",
        msg: "Please fix the errors below before saving.",
      });
      return;
    }
    setSaving(true);
    try {
      // Fix 5: detect concurrent edits — re-fetch latest before saving and warn if it differs
      const [latestWork, latestSkills] = await Promise.all([
        api.get("/api/employee-experience/work"),
        api.get("/api/employee-experience/skills"),
      ]);
      const latestEmpSnap =
        latestWork.data.length > 0 ? latestWork.data.map(rowToEmployment) : [];
      const latestSkillSnap =
        latestSkills.data.length > 0 ? latestSkills.data.map(rowToSkill) : [];
      const serverChanged =
        JSON.stringify(latestEmpSnap) !==
          JSON.stringify(originalData.employments) ||
        JSON.stringify(latestSkillSnap) !== JSON.stringify(originalData.skills);
      if (serverChanged) {
        setSaving(false);
        setAlert({
          type: "warning",
          msg: "Your data was updated elsewhere since you opened this page. Please refresh and re-apply your changes to avoid overwriting.",
        });
        return;
      }

      await api.post("/api/employee-experience/save-all", {
        employments,
        skills,
      });
      setAlert({ type: "success", msg: "Details saved successfully." });
      // Reload to get DB-assigned IDs
      const [workRes, skillsRes] = await Promise.all([
        api.get("/api/employee-experience/work"),
        api.get("/api/employee-experience/skills"),
      ]);
      if (workRes.data.length > 0)
        setEmployments(workRes.data.map(rowToEmployment));
      if (skillsRes.data.length > 0) setSkills(skillsRes.data.map(rowToSkill));
      // update original snapshot after save
      const empSnap =
        workRes.data.length > 0 ? workRes.data.map(rowToEmployment) : [];
      const skillSnap =
        skillsRes.data.length > 0 ? skillsRes.data.map(rowToSkill) : [];
      setOriginalData({ employments: empSnap, skills: skillSnap });
    } catch (err) {
      setAlert({
        type: "error",
        msg: err.response?.data?.message || "Failed to save. Please try again.",
      });
    } finally {
      setSaving(false);
      setTimeout(() => setAlert({ type: "", msg: "" }), 5000);
    }
  };

  return (
    <Container maxWidth="xl" disableGutters sx={{ pb: 6 }}>
      {/* ── Page Header ── */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #1a3a5c 0%, #1976d2 100%)",
          borderRadius: 2,
          px: 3,
          py: 2,
          mb: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="#fff">
            Employee Experience Details &amp; Skills
          </Typography>
          <Typography variant="caption" color="rgba(255,255,255,0.75)">
            {activeTab === 0
              ? "Add your previous employment history, current company details, and skills summary."
              : "Search and browse employee skills across the organisation."}
          </Typography>
        </Box>
        {activeTab === 0 ? (
          <Button
            variant="contained"
            onClick={addEmployment}
            sx={{
              backgroundColor: "#fff",
              color: "#1565c0",
              fontWeight: 700,
              borderRadius: 2,
              "&:hover": { backgroundColor: "#e3f2fd" },
            }}
          >
            + Add Employment
          </Button>
        ) : (
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            disabled={(smSearched ? smResults : smAllData).length === 0}
            onClick={() => exportCSV(smSearched ? smResults : smAllData)}
            sx={{
              backgroundColor: "#fff",
              color: "#1565c0",
              fontWeight: 700,
              borderRadius: 2,
              "&:hover": { backgroundColor: "#e3f2fd" },
            }}
          >
            Export CSV
          </Button>
        )}
      </Box>

      {/* ── Tabs ── */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "0 0 0 0",
          borderBottom: "1px solid #e0e8f0",
          mb: 3,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            px: 2,
            "& .MuiTab-root": {
              fontWeight: 600,
              fontSize: 13,
              textTransform: "none",
              minHeight: 48,
            },
            "& .Mui-selected": { color: "#1565c0" },
            "& .MuiTabs-indicator": { backgroundColor: "#1565c0" },
          }}
        >
          <Tab
            icon={<WorkHistoryOutlinedIcon sx={{ fontSize: 16 }} />}
            iconPosition="start"
            label="My Experience"
          />
          {isHR && (
            <Tab
              icon={<PsychologyOutlinedIcon sx={{ fontSize: 16 }} />}
              iconPosition="start"
              label="Search Employees"
            />
          )}
        </Tabs>
      </Paper>

      {alert.msg && (
        <Alert
          severity={alert.type}
          sx={{ mb: 2 }}
          onClose={() => setAlert({ type: "", msg: "" })}
        >
          {alert.msg}
        </Alert>
      )}

      {/* ── Tab 0: My Experience (Employee form) ── */}
      {activeTab === 0 && (
        <>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={200}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* ── Experience Summary ── */}
              <SectionCard>
                <SectionTitle>
                  <Box display="flex" alignItems="center" gap={1}>
                    <WorkHistoryOutlinedIcon
                      sx={{ color: "#1976d2", fontSize: 20 }}
                    />
                    Experience Summary
                  </Box>
                </SectionTitle>
                <Box display="flex" flexWrap="wrap" gap={1.5} mb={2}>
                  <StatChip label="Total Experience" value={totalExp} />
                  <StatChip
                    label="IT Experience"
                    value={itExp}
                    color="#2e7d32"
                  />
                  <StatChip
                    label="Non-IT Experience"
                    value={nonItExp}
                    color="#e65100"
                  />
                  <StatChip
                    label="Companies Worked"
                    value={`${employments.length} Companies`}
                    color="#6a1b9a"
                  />
                  <StatChip
                    label="Technologies"
                    value={`${[...new Set(employments.flatMap((e) => e.technologies))].length}`}
                    color="#00838f"
                  />
                  <StatChip
                    label="Skills Listed"
                    value={`${skills.filter((s) => s.technology).length}`}
                    color="#c62828"
                  />
                </Box>
                {currentEmp && (
                  <Box
                    sx={{
                      background: "#f0f7ff",
                      borderRadius: 1.5,
                      px: 2,
                      py: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <CalendarTodayOutlinedIcon
                      sx={{ fontSize: 14, color: "#1976d2" }}
                    />
                    <Typography variant="caption" color="#1565c0">
                      Career Timeline:{" "}
                      {currentEmp.start_date
                        ? new Date(currentEmp.start_date).toLocaleDateString(
                            "en-US",
                            { month: "short", year: "numeric" },
                          )
                        : "—"}{" "}
                      – Present (Active)
                    </Typography>
                    <Box flexGrow={1} />
                    <Typography variant="caption" color="text.secondary">
                      Current Tenure: {calcDuration(currentEmp.start_date, "")}
                    </Typography>
                  </Box>
                )}
              </SectionCard>

              {/* ── Employment History ── */}
              <SectionCard>
                <SectionTitle
                  action={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={`${employments.filter((e) => !e.is_current).length} Previous Companies`}
                        size="small"
                        sx={{
                          backgroundColor: "#e3f2fd",
                          color: "#1565c0",
                          fontWeight: 600,
                        }}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={addEmployment}
                        sx={{
                          borderRadius: 2,
                          borderColor: "#1976d2",
                          color: "#1976d2",
                          fontWeight: 600,
                        }}
                      >
                        Add Previous Employment
                      </Button>
                    </Box>
                  }
                >
                  Previous Employment History
                </SectionTitle>

                {employments.length === 0 ? (
                  <Box
                    sx={{ textAlign: "center", py: 4, color: "text.secondary" }}
                  >
                    <Typography variant="body2">
                      No employment history added yet. Click "Add Previous
                      Employment" to get started.
                    </Typography>
                  </Box>
                ) : (
                  employments.map((emp, idx) => (
                    <Box
                      key={emp.id}
                      sx={{
                        border: "1px solid #d0e4f7",
                        borderRadius: 2,
                        p: 2,
                        mb: 2,
                        backgroundColor: emp.is_current ? "#f0fff4" : "#fafcff",
                        position: "relative",
                      }}
                    >
                      {/* Company badge */}
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={1.5}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={`Company ${idx + 1} of ${employments.length}`}
                            size="small"
                            sx={{
                              backgroundColor: emp.is_current
                                ? "#c8e6c9"
                                : "#bbdefb",
                              color: emp.is_current ? "#1b5e20" : "#0d47a1",
                              fontWeight: 700,
                              fontSize: 11,
                            }}
                          />
                          {emp.is_current && (
                            <Chip
                              icon={
                                <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
                              }
                              label="Currently Working"
                              size="small"
                              sx={{
                                backgroundColor: "#e8f5e9",
                                color: "#2e7d32",
                                fontWeight: 600,
                                fontSize: 11,
                              }}
                            />
                          )}
                        </Box>
                        <Box display="flex" gap={1}>
                          <Button
                            size="small"
                            variant={emp.is_current ? "contained" : "outlined"}
                            onClick={() =>
                              setEmployments((prev) =>
                                prev.map((e) =>
                                  e.id === emp.id
                                    ? {
                                        ...e,
                                        is_current: !e.is_current,
                                        end_date: "",
                                      }
                                    : { ...e, is_current: false },
                                ),
                              )
                            }
                            sx={{
                              fontSize: 11,
                              borderRadius: 2,
                              ...(emp.is_current
                                ? {
                                    backgroundColor: "#2e7d32",
                                    "&:hover": { backgroundColor: "#1b5e20" },
                                  }
                                : { borderColor: "#2e7d32", color: "#2e7d32" }),
                            }}
                          >
                            {emp.is_current ? "✓ Current" : "Set as Current"}
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            startIcon={<DeleteOutlineIcon />}
                            onClick={() => removeEmployment(emp.id)}
                            sx={{ fontSize: 11, borderRadius: 2 }}
                          >
                            Remove
                          </Button>
                        </Box>
                      </Box>

                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="Company Name"
                            value={emp.company_name}
                            onChange={(e) =>
                              updateEmp(emp.id, "company_name", e.target.value)
                            }
                            fullWidth
                            size="small"
                            required
                            InputLabelProps={{ shrink: true }}
                            error={!!errors[`emp_${emp.id}_company_name`]}
                            helperText={errors[`emp_${emp.id}_company_name`]}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="Job Title"
                            value={emp.job_title}
                            onChange={(e) =>
                              updateEmp(emp.id, "job_title", e.target.value)
                            }
                            fullWidth
                            size="small"
                            required
                            InputLabelProps={{ shrink: true }}
                            error={!!errors[`emp_${emp.id}_job_title`]}
                            helperText={errors[`emp_${emp.id}_job_title`]}
                          />
                        </Grid>
                        {/* <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Employment Type</InputLabel>
                  <Select
                    value={emp.employment_type}
                    label="Employment Type"
                    onChange={(e) => updateEmp(emp.id, "employment_type", e.target.value)}
                  >
                    {empTypeOptions.map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid> */}
                        <Grid item xs={12} sm={4}>
                          <FormControl
                            fullWidth
                            size="small"
                            sx={{ minWidth: 180 }}
                            required
                            error={!!errors[`emp_${emp.id}_employment_type`]}
                          >
                            <InputLabel id="employment-type-label">
                              Employment Type
                            </InputLabel>
                            <Select
                              labelId="employment-type-label"
                              value={emp.employment_type}
                              label="Employment Type"
                              onChange={(e) =>
                                updateEmp(
                                  emp.id,
                                  "employment_type",
                                  e.target.value,
                                )
                              }
                            >
                              {empTypeOptions.map((t) => (
                                <MenuItem key={t} value={t}>
                                  {t}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors[`emp_${emp.id}_employment_type`] && (
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{ mt: 0.5, ml: 1.5 }}
                              >
                                {errors[`emp_${emp.id}_employment_type`]}
                              </Typography>
                            )}
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={3}>
                          <TextField
                            label="Start Date"
                            type="month"
                            value={emp.start_date}
                            onChange={(e) =>
                              updateEmp(emp.id, "start_date", e.target.value)
                            }
                            fullWidth
                            size="small"
                            required
                            InputLabelProps={{ shrink: true }}
                            error={!!errors[`emp_${emp.id}_start_date`]}
                            helperText={errors[`emp_${emp.id}_start_date`]}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            label={
                              emp.is_current ? "End Date (Present)" : "End Date"
                            }
                            type="month"
                            value={emp.is_current ? "" : emp.end_date}
                            onChange={(e) =>
                              updateEmp(emp.id, "end_date", e.target.value)
                            }
                            fullWidth
                            size="small"
                            required={!emp.is_current}
                            disabled={emp.is_current}
                            InputLabelProps={{ shrink: true }}
                            placeholder={emp.is_current ? "Present" : ""}
                            error={!!errors[`emp_${emp.id}_end_date`]}
                            helperText={errors[`emp_${emp.id}_end_date`]}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            label="Duration"
                            value={calcDuration(
                              emp.start_date,
                              emp.is_current ? "" : emp.end_date,
                            )}
                            fullWidth
                            size="small"
                            disabled
                            InputProps={{ readOnly: true }}
                            InputLabelProps={{ shrink: true }}
                            sx={{
                              "& .MuiInputBase-input.Mui-disabled": {
                                color: "#1976d2",
                                WebkitTextFillColor: "#1976d2",
                                fontWeight: 600,
                              },
                            }}
                          />
                        </Grid>
                        {/* Technologies Used */}
                        <Grid item xs={12} sm={3}>
                          <FormControl
                            fullWidth
                            size="small"
                            sx={{ minWidth: 210 }}
                            required
                            error={!!errors[`emp_${emp.id}_technologies`]}
                          >
                            <InputLabel
                              id="technology-label"
                              shrink
                              sx={{ backgroundColor: "#fff", px: 0.5 }}
                            >
                              {emp.employment_type === "Non-IT"
                                ? "Domain / Function"
                                : "Technologies Used"}
                            </InputLabel>
                            <Select
                              labelId="technology-label"
                              id="technology-select"
                              multiple
                              value={emp.technologies}
                              label={
                                emp.employment_type === "Non-IT"
                                  ? "Domain / Function"
                                  : "Technologies Used"
                              }
                              onChange={(e) =>
                                updateEmp(
                                  emp.id,
                                  "technologies",
                                  e.target.value,
                                )
                              }
                              renderValue={(selected) => (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.5,
                                  }}
                                >
                                  {selected.map((value) => (
                                    <Chip
                                      key={value}
                                      label={value}
                                      size="small"
                                      onDelete={(e) => {
                                        e.stopPropagation();
                                        updateEmp(
                                          emp.id,
                                          "technologies",
                                          emp.technologies.filter(
                                            (t) => t !== value,
                                          ),
                                        );
                                      }}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      sx={{
                                        backgroundColor:
                                          emp.employment_type === "Non-IT"
                                            ? "#fff3e0"
                                            : "#e3f2fd",
                                        color:
                                          emp.employment_type === "Non-IT"
                                            ? "#e65100"
                                            : "#1565c0",
                                        fontWeight: 600,
                                      }}
                                    />
                                  ))}
                                </Box>
                              )}
                            >
                              {(emp.employment_type === "Non-IT"
                                ? nonItOptions
                                : techOptions
                              ).map((tech) => (
                                <MenuItem
                                  key={tech}
                                  value={tech}
                                  sx={{
                                    backgroundColor: emp.technologies.includes(
                                      tech,
                                    )
                                      ? emp.employment_type === "Non-IT"
                                        ? "#fff3e0"
                                        : "#e3f2fd"
                                      : "inherit",
                                    "&:hover": {
                                      backgroundColor:
                                        emp.employment_type === "Non-IT"
                                          ? "#ffe0b2"
                                          : "#bbdefb",
                                    },
                                  }}
                                >
                                  <Checkbox
                                    checked={emp.technologies.includes(tech)}
                                    size="small"
                                    sx={{
                                      p: 0.5,
                                      color:
                                        emp.employment_type === "Non-IT"
                                          ? "#e65100"
                                          : "#1976d2",
                                      "&.Mui-checked": {
                                        color:
                                          emp.employment_type === "Non-IT"
                                            ? "#e65100"
                                            : "#1976d2",
                                      },
                                    }}
                                  />
                                  <ListItemText
                                    primary={tech}
                                    primaryTypographyProps={{ fontSize: 13 }}
                                  />
                                </MenuItem>
                              ))}
                            </Select>
                            {errors[`emp_${emp.id}_technologies`] && (
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{ mt: 0.5, ml: 1.5 }}
                              >
                                {errors[`emp_${emp.id}_technologies`]}
                              </Typography>
                            )}
                          </FormControl>
                        </Grid>
                        {/* Others */}
                        <Grid item xs={12} sm={3}>
                          <TextField
                            label="Others (e.g. HTML, CSS)"
                            value={emp.others}
                            onChange={(e) =>
                              updateEmp(emp.id, "others", e.target.value)
                            }
                            fullWidth
                            size="small"
                            placeholder="e.g. JavaScript, AWS"
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                      </Grid>

                      {/* Background Verification */}
                      <Divider sx={{ my: 1.5 }} />
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        color="#1a3a5c"
                        display="block"
                        mb={1}
                      >
                        Background Verification Details
                      </Typography>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            label="Reference Person"
                            value={emp.ref_person}
                            onChange={(e) =>
                              updateEmp(emp.id, "ref_person", e.target.value)
                            }
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            error={!!errors[`emp_${emp.id}_ref_person`]}
                            helperText={errors[`emp_${emp.id}_ref_person`]}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            label="Relationship"
                            value={emp.relationship}
                            onChange={(e) =>
                              updateEmp(emp.id, "relationship", e.target.value)
                            }
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            error={!!errors[`emp_${emp.id}_relationship`]}
                            helperText={errors[`emp_${emp.id}_relationship`]}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            label="Contact Number"
                            value={emp.contact_number}
                            onChange={(e) => {
                              const val = e.target.value
                                .replace(/[^0-9]/g, "")
                                .slice(0, 10);
                              updateEmp(emp.id, "contact_number", val);
                            }}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ maxLength: 10 }}
                            error={!!errors[`emp_${emp.id}_contact_number`]}
                            helperText={errors[`emp_${emp.id}_contact_number`]}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            label="Email"
                            value={emp.email}
                            onChange={(e) =>
                              updateEmp(emp.id, "email", e.target.value.trim())
                            }
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            error={!!errors[`emp_${emp.id}_email`]}
                            helperText={errors[`emp_${emp.id}_email`]}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            label="Company Address"
                            value={emp.company_address}
                            onChange={(e) =>
                              updateEmp(
                                emp.id,
                                "company_address",
                                e.target.value,
                              )
                            }
                            fullWidth
                            size="small"
                            placeholder="e.g. 123 Tech Park, Bangalore, Karnataka 560001"
                            InputLabelProps={{ shrink: true }}
                            error={!!errors[`emp_${emp.id}_company_address`]}
                            helperText={errors[`emp_${emp.id}_company_address`]}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  ))
                )}
              </SectionCard>

              {/* ── Skills Details ── */}
              <SectionCard>
                <SectionTitle
                  action={
                    <Button
                      size="small"
                      variant="outlined"
                      // startIcon={<AddIcon />}
                      onClick={addSkill}
                      sx={{
                        borderRadius: 2,
                        borderColor: "#1976d2",
                        color: "#1976d2",
                        fontWeight: 600,
                      }}
                    >
                      + Add Technology
                    </Button>
                  }
                >
                  Skills Details
                </SectionTitle>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mb={1.5}
                >
                  Enter your experience by technology. You can add multiple
                  technologies and specify your experience level for each.
                </Typography>

                {/* Table header */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "2fr 2fr 1fr 1fr 1fr 1fr 60px",
                    gap: 1,
                    px: 1,
                    py: 0.5,
                    backgroundColor: "#1a3a5c",
                    borderRadius: "8px 8px 0 0",
                  }}
                >
                  {[
                    "Technology",
                    "Technology Details",
                    "Experience (Years)",
                    "Experience (Months)",
                    "Total",
                    "Level",
                    "Actions",
                  ].map((h) => (
                    <Typography
                      key={h}
                      variant="caption"
                      fontWeight={700}
                      color="#fff"
                      sx={{ fontSize: 12 }}
                    >
                      {h}
                    </Typography>
                  ))}
                </Box>

                {skills.length === 0 ? (
                  <Box
                    sx={{ textAlign: "center", py: 3, color: "text.secondary" }}
                  >
                    <Typography variant="body2">
                      No skills added yet. Click "+ Add Technology" to get
                      started.
                    </Typography>
                  </Box>
                ) : (
                  skills.map((skill, idx) => (
                    <Box
                      key={skill.id}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "2fr 2fr 1fr 1fr 1fr 1fr 60px",
                        gap: 1,
                        px: 1,
                        py: 0.75,
                        alignItems: "center",
                        backgroundColor: idx % 2 === 0 ? "#fafcff" : "#fff",
                        borderBottom: "1px solid #e8f0fe",
                      }}
                    >
                      <Box>
                        <FormControl
                          size="small"
                          fullWidth
                          required
                          error={!!errors[`skill_${skill.id}_technology`]}
                        >
                          <Select
                            value={skill.technology}
                            displayEmpty
                            onChange={(e) =>
                              updateSkill(
                                skill.id,
                                "technology",
                                e.target.value,
                              )
                            }
                            renderValue={(v) =>
                              v || (
                                <span style={{ color: "#aaa" }}>
                                  Select Technology
                                </span>
                              )
                            }
                          >
                            {techOptions.map((t) => (
                              <MenuItem key={t} value={t}>
                                {t}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {errors[`skill_${skill.id}_technology`] && (
                          <Typography variant="caption" color="error">
                            {errors[`skill_${skill.id}_technology`]}
                          </Typography>
                        )}
                      </Box>

                      {/* Technology Details */}
                      <Box>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="e.g. React 18, Node.js v20"
                          value={skill.technology_details}
                          onChange={(e) =>
                            updateSkill(
                              skill.id,
                              "technology_details",
                              e.target.value,
                            )
                          }
                        />
                      </Box>

                      <Box>
                        <TextField
                          size="small"
                          type="number"
                          value={skill.exp_years}
                          onChange={(e) =>
                            updateSkill(skill.id, "exp_years", e.target.value)
                          }
                          inputProps={{ min: 0, max: 40 }}
                          placeholder="0"
                          fullWidth
                          error={!!errors[`skill_${skill.id}_exp`]}
                        />
                      </Box>

                      <Box>
                        <TextField
                          size="small"
                          type="number"
                          value={skill.exp_months}
                          onChange={(e) =>
                            updateSkill(skill.id, "exp_months", e.target.value)
                          }
                          inputProps={{ min: 0, max: 11 }}
                          placeholder="0"
                          fullWidth
                          error={!!errors[`skill_${skill.id}_exp`]}
                        />
                        {errors[`skill_${skill.id}_exp`] && (
                          <Typography variant="caption" color="error">
                            {errors[`skill_${skill.id}_exp`]}
                          </Typography>
                        )}
                      </Box>

                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="#1565c0"
                      >
                        {skillTotal(skill)}
                      </Typography>

                      <FormControl size="small" fullWidth>
                        <Select
                          value={skill.level}
                          onChange={(e) =>
                            updateSkill(skill.id, "level", e.target.value)
                          }
                        >
                          {levelOptions.map((l) => (
                            <MenuItem key={l} value={l}>
                              {l}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Tooltip title="Remove">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeSkill(skill.id)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))
                )}

                {/* Skills total summary */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mt: 1.5,
                    px: 1.5,
                    py: 1,
                    backgroundColor: "#e8f5e9",
                    borderRadius: 1.5,
                    border: "1px solid #c8e6c9",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <CheckCircleOutlineIcon
                      sx={{ color: "#2e7d32", fontSize: 18 }}
                    />
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="#2e7d32"
                    >
                      Total Experience Matches Summary: {totalExp}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={saving || (hasExistingData && !hasChanges)}
                    sx={{
                      backgroundColor: "#1565c0",
                      borderRadius: 2,
                      fontWeight: 700,
                      "&:hover": { backgroundColor: "#0d47a1" },
                    }}
                  >
                    {saving
                      ? "Saving..."
                      : hasExistingData
                        ? "Update All Details"
                        : "Save All Details"}
                  </Button>
                </Box>
              </SectionCard>
            </>
          )}
        </>
      )}

      {/* ── Tab 1: Skill Matrix (HR only) ── */}
      {isHR && activeTab === 1 && (
        <>
          {smError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setSmError("")}
            >
              {smError}
            </Alert>
          )}

          {/* ── Filter panel ── */}
          <Paper
            elevation={0}
            sx={{
              border: "1px solid #e0e8f0",
              borderRadius: 2,
              p: 2.5,
              mb: 2.5,
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <FilterListIcon sx={{ color: "#1976d2", fontSize: 20 }} />
              <Typography variant="subtitle2" fontWeight={700} color="#1a3a5c">
                Filter by Skill
              </Typography>
              {(smSelectedTechs.length > 0 ||
                smSelectedLevel ||
                smMinYears) && (
                <Chip
                  label="Clear filters"
                  size="small"
                  icon={<ClearIcon sx={{ fontSize: 14 }} />}
                  onClick={handleSmClear}
                  sx={{
                    ml: 1,
                    cursor: "pointer",
                    backgroundColor: "#fce4ec",
                    color: "#c62828",
                  }}
                />
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "flex-end",
              }}
            >
              {/* Technology multi-select */}
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel shrink sx={{ backgroundColor: "#fff", px: 0.5 }}>
                  Technologies
                </InputLabel>
                <Select
                  multiple
                  value={smSelectedTechs}
                  label="Technologies"
                  onChange={(e) => setSmSelectedTechs(e.target.value)}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4 }}>
                      {selected.map((v) => (
                        <Chip
                          key={v}
                          label={v}
                          size="small"
                          onDelete={(e) => {
                            e.stopPropagation();
                            setSmSelectedTechs((p) => p.filter((t) => t !== v));
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          sx={{
                            backgroundColor: "#e3f2fd",
                            color: "#1565c0",
                            fontWeight: 600,
                            height: 20,
                            fontSize: 11,
                          }}
                        />
                      ))}
                    </Box>
                  )}
                >
                  {techOptions.map((t) => (
                    <MenuItem key={t} value={t}>
                      <Checkbox
                        checked={smSelectedTechs.includes(t)}
                        size="small"
                        sx={{
                          p: 0.5,
                          color: "#1976d2",
                          "&.Mui-checked": { color: "#1976d2" },
                        }}
                      />
                      <ListItemText
                        primary={t}
                        primaryTypographyProps={{ fontSize: 13 }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Skill level */}
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Skill Level</InputLabel>
                <Select
                  value={smSelectedLevel}
                  label="Skill Level"
                  onChange={(e) => setSmSelectedLevel(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Any level</em>
                  </MenuItem>
                  {levelOptions.map((l) => (
                    <MenuItem key={l} value={l}>
                      {l}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Min years */}
              <TextField
                size="small"
                label="Min. Years Experience"
                type="number"
                value={smMinYears}
                onChange={(e) => setSmMinYears(e.target.value)}
                inputProps={{ min: 0, max: 40 }}
                sx={{ width: 180 }}
                InputLabelProps={{ shrink: true }}
              />

              {/* Match mode */}
              {smSelectedTechs.length > 1 && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    mb={0.5}
                  >
                    Match mode
                  </Typography>
                  <ToggleButtonGroup
                    size="small"
                    value={smMatchMode}
                    exclusive
                    onChange={(_, val) => val && setSmMatchMode(val)}
                    sx={{ height: 36 }}
                  >
                    <ToggleButton
                      value="any"
                      sx={{ px: 1.5, fontSize: 12, textTransform: "none" }}
                    >
                      Any skill
                    </ToggleButton>
                    <ToggleButton
                      value="all"
                      sx={{ px: 1.5, fontSize: 12, textTransform: "none" }}
                    >
                      All skills
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              )}

              {/* Search / Clear buttons */}
              <Button
                variant="contained"
                onClick={handleSkillSearch}
                disabled={smLoading || smSelectedTechs.length === 0}
                sx={{
                  backgroundColor: "#1565c0",
                  borderRadius: 2,
                  fontWeight: 700,
                  height: 36,
                  "&:hover": { backgroundColor: "#0d47a1" },
                }}
              >
                {smLoading ? (
                  <CircularProgress size={18} sx={{ color: "#fff" }} />
                ) : (
                  "Search"
                )}
              </Button>
              {smSearched && (
                <Button
                  variant="outlined"
                  onClick={handleSmClear}
                  sx={{ borderRadius: 2, height: 36 }}
                >
                  Show All
                </Button>
              )}
            </Box>
          </Paper>

          {/* ── Summary stats ── */}
          {!smAllLoading &&
            (() => {
              const displayList = smSearched ? smResults : smAllData;
              const filteredList = smNameSearch.trim()
                ? displayList.filter(
                    (emp) =>
                      emp.emp_name
                        ?.toLowerCase()
                        .includes(smNameSearch.toLowerCase()) ||
                      String(emp.emp_id).includes(smNameSearch),
                  )
                : displayList;
              const allSkillsFlat = filteredList.flatMap(
                (e) => e.matched_skills || e.skills || [],
              );
              const uniqueTechs = new Set(
                allSkillsFlat.map((s) => s.technology),
              ).size;
              const expertCount = allSkillsFlat.filter(
                (s) => s.level === "Expert",
              ).length;
              return (
                <Box display="flex" flexWrap="wrap" gap={1.5} mb={2.5}>
                  <StatCard
                    icon={<PeopleAltOutlinedIcon />}
                    label="Employees shown"
                    value={filteredList.length}
                  />
                  <StatCard
                    icon={<PsychologyOutlinedIcon />}
                    label="Unique technologies"
                    value={uniqueTechs}
                    color="#00838f"
                  />
                  <StatCard
                    icon={<StarOutlineIcon />}
                    label="Expert-level skills"
                    value={expertCount}
                    color="#e65100"
                  />
                  {smSearched && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        px: 2,
                        py: 1,
                        backgroundColor: "#e8f5e9",
                        border: "1px solid #c8e6c9",
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="#2e7d32"
                      >
                        {filteredList.length} employee
                        {filteredList.length !== 1 ? "s" : ""} matched your
                        search
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })()}

          {/* ── Name search bar ── */}
          <Box mb={1.5}>
            <TextField
              placeholder="Search by employee name or ID..."
              size="small"
              value={smNameSearch}
              onChange={(e) => setSmNameSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 320 }}
            />
          </Box>

          {/* ── Results table ── */}
          {smAllLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : (
            (() => {
              const displayList = smSearched ? smResults : smAllData;
              const filteredList = smNameSearch.trim()
                ? displayList.filter(
                    (emp) =>
                      emp.emp_name
                        ?.toLowerCase()
                        .includes(smNameSearch.toLowerCase()) ||
                      String(emp.emp_id).includes(smNameSearch),
                  )
                : displayList;

              return (
                <Paper
                  elevation={0}
                  sx={{
                    border: "1px solid #e0e8f0",
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#1a3a5c" }}>
                          <TableCell
                            sx={{ color: "#fff", fontWeight: 700, width: 40 }}
                          />
                          <TableCell sx={{ color: "#fff", fontWeight: 700 }}>
                            Employee
                          </TableCell>
                          <TableCell sx={{ color: "#fff", fontWeight: 700 }}>
                            {smSearched ? "Matched Skills" : "Technologies"}
                          </TableCell>
                          <TableCell sx={{ color: "#fff", fontWeight: 700 }}>
                            {smSearched ? "Skill Count" : "Total Exp"}
                          </TableCell>
                          <TableCell sx={{ color: "#fff", fontWeight: 700 }}>
                            {smSearched ? "Top Level" : "IT Exp"}
                          </TableCell>
                          <TableCell sx={{ color: "#fff", fontWeight: 700 }}>
                            Companies
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {filteredList.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={6}
                              align="center"
                              sx={{ py: 4 }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {smSearched
                                  ? "No employees matched the selected filters."
                                  : "No employee skill data found."}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredList.map((row) => {
                            const skillList =
                              row.matched_skills || row.skills || [];
                            const techList =
                              row.all_technologies ||
                              skillList.map((s) => s.technology);
                            const topLevel =
                              skillList.find((s) => s.level === "Expert")
                                ?.level ||
                              skillList.find((s) => s.level === "Intermediate")
                                ?.level ||
                              skillList[0]?.level ||
                              "—";
                            const lvlStyle = levelStyle(topLevel);
                            const isExpanded = smExpandedRow === row.emp_id;

                            return (
                              <React.Fragment key={row.emp_id}>
                                <TableRow
                                  hover
                                  onClick={() =>
                                    setSmExpandedRow(
                                      isExpanded ? null : row.emp_id,
                                    )
                                  }
                                  sx={{
                                    cursor: "pointer",
                                    backgroundColor: isExpanded
                                      ? "#f0f7ff"
                                      : "inherit",
                                  }}
                                >
                                  <TableCell>
                                    <IconButton size="small">
                                      {isExpanded ? (
                                        <KeyboardArrowUpIcon fontSize="small" />
                                      ) : (
                                        <KeyboardArrowDownIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </TableCell>

                                  <TableCell>
                                    <Typography
                                      variant="body2"
                                      fontWeight={600}
                                    >
                                      {row.emp_name}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      ID: {row.emp_id}
                                    </Typography>
                                  </TableCell>

                                  <TableCell>
                                    <Box
                                      display="flex"
                                      flexWrap="wrap"
                                      gap={0.4}
                                    >
                                      {techList.slice(0, 5).map((t) => (
                                        <Chip
                                          key={t}
                                          label={t}
                                          size="small"
                                          sx={{
                                            fontSize: 10,
                                            height: 20,
                                            backgroundColor:
                                              smSelectedTechs.includes(t)
                                                ? "#1976d2"
                                                : "#e3f2fd",
                                            color: smSelectedTechs.includes(t)
                                              ? "#fff"
                                              : "#1565c0",
                                            fontWeight:
                                              smSelectedTechs.includes(t)
                                                ? 700
                                                : 400,
                                          }}
                                        />
                                      ))}
                                      {techList.length > 5 && (
                                        <Chip
                                          label={`+${techList.length - 5}`}
                                          size="small"
                                          sx={{ fontSize: 10, height: 20 }}
                                        />
                                      )}
                                    </Box>
                                  </TableCell>

                                  <TableCell>
                                    <Typography variant="body2">
                                      {smSearched
                                        ? `${skillList.length} skill${skillList.length !== 1 ? "s" : ""}`
                                        : row.total_exp || "—"}
                                    </Typography>
                                  </TableCell>

                                  <TableCell>
                                    {smSearched ? (
                                      <Chip
                                        label={topLevel}
                                        size="small"
                                        sx={{
                                          fontSize: 10,
                                          height: 20,
                                          backgroundColor: lvlStyle.bg,
                                          color: lvlStyle.text,
                                          fontWeight: 700,
                                        }}
                                      />
                                    ) : (
                                      <Typography variant="body2">
                                        {row.it_exp || "—"}
                                      </Typography>
                                    )}
                                  </TableCell>

                                  <TableCell>
                                    <Typography variant="body2">
                                      {row.company_count ??
                                        row.employments?.length ??
                                        "—"}
                                    </Typography>
                                  </TableCell>
                                </TableRow>

                                {/* ── Expanded detail row ── */}
                                <TableRow>
                                  <TableCell
                                    colSpan={6}
                                    sx={{ p: 0, border: 0 }}
                                  >
                                    <Collapse
                                      in={isExpanded}
                                      timeout="auto"
                                      unmountOnExit
                                    >
                                      <Box
                                        sx={{
                                          px: 3,
                                          py: 2,
                                          backgroundColor: "#fafcff",
                                          borderBottom: "1px solid #e0e8f0",
                                        }}
                                      >
                                        {/* Skills */}
                                        <Typography
                                          variant="caption"
                                          fontWeight={700}
                                          color="#1a3a5c"
                                          display="block"
                                          mb={1}
                                        >
                                          <PsychologyOutlinedIcon
                                            sx={{
                                              fontSize: 13,
                                              mr: 0.5,
                                              verticalAlign: "middle",
                                            }}
                                          />
                                          Skills
                                        </Typography>
                                        {skillList.length === 0 ? (
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            No skill data recorded.
                                          </Typography>
                                        ) : (
                                          <Box
                                            display="flex"
                                            flexWrap="wrap"
                                            gap={1}
                                            mb={2}
                                          >
                                            {skillList.map((s, i) => {
                                              const ls = levelStyle(s.level);
                                              return (
                                                <Box
                                                  key={i}
                                                  sx={{
                                                    border: `1px solid ${ls.bg}`,
                                                    borderRadius: 1.5,
                                                    px: 1.5,
                                                    py: 0.75,
                                                    backgroundColor: ls.bg,
                                                    minWidth: 130,
                                                  }}
                                                >
                                                  <Typography
                                                    variant="body2"
                                                    fontWeight={700}
                                                    color={ls.text}
                                                    sx={{ fontSize: 12 }}
                                                  >
                                                    {s.technology}
                                                  </Typography>
                                                  <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                  >
                                                    {fmtExp(
                                                      s.exp_years,
                                                      s.exp_months,
                                                    )}{" "}
                                                    · {s.level}
                                                  </Typography>
                                                </Box>
                                              );
                                            })}
                                          </Box>
                                        )}

                                        {/* Work history */}
                                        {(row.employments || []).length > 0 && (
                                          <>
                                            <Divider sx={{ mb: 1.5 }} />
                                            <Typography
                                              variant="caption"
                                              fontWeight={700}
                                              color="#1a3a5c"
                                              display="block"
                                              mb={1}
                                            >
                                              <WorkHistoryOutlinedIcon
                                                sx={{
                                                  fontSize: 13,
                                                  mr: 0.5,
                                                  verticalAlign: "middle",
                                                }}
                                              />
                                              Work History
                                            </Typography>
                                            <Table size="small">
                                              <TableHead>
                                                <TableRow
                                                  sx={{
                                                    backgroundColor: "#e8f0fe",
                                                  }}
                                                >
                                                  {[
                                                    "Company",
                                                    "Role",
                                                    "Type",
                                                    "Period",
                                                    "Duration",
                                                  ].map((h) => (
                                                    <TableCell
                                                      key={h}
                                                      sx={{
                                                        fontSize: 11,
                                                        fontWeight: 700,
                                                        py: 0.5,
                                                      }}
                                                    >
                                                      {h}
                                                    </TableCell>
                                                  ))}
                                                </TableRow>
                                              </TableHead>
                                              <TableBody>
                                                {(row.employments || []).map(
                                                  (emp, i) => {
                                                    const start = emp.start_date
                                                      ? new Date(
                                                          emp.start_date,
                                                        ).toLocaleDateString(
                                                          "en-US",
                                                          {
                                                            month: "short",
                                                            year: "numeric",
                                                          },
                                                        )
                                                      : "—";
                                                    const end = emp.is_current
                                                      ? "Present"
                                                      : emp.end_date
                                                        ? new Date(
                                                            emp.end_date,
                                                          ).toLocaleDateString(
                                                            "en-US",
                                                            {
                                                              month: "short",
                                                              year: "numeric",
                                                            },
                                                          )
                                                        : "—";
                                                    const s = emp.start_date
                                                      ? new Date(emp.start_date)
                                                      : null;
                                                    const e = emp.is_current
                                                      ? new Date()
                                                      : emp.end_date
                                                        ? new Date(emp.end_date)
                                                        : null;
                                                    let dur = "—";
                                                    if (s && e) {
                                                      const mo =
                                                        (e.getFullYear() -
                                                          s.getFullYear()) *
                                                          12 +
                                                        (e.getMonth() -
                                                          s.getMonth());
                                                      if (mo >= 0) {
                                                        const y = Math.floor(
                                                            mo / 12,
                                                          ),
                                                          m = mo % 12;
                                                        dur =
                                                          [
                                                            y > 0
                                                              ? `${y}y`
                                                              : "",
                                                            m > 0
                                                              ? `${m}m`
                                                              : "",
                                                          ]
                                                            .filter(Boolean)
                                                            .join(" ") ||
                                                          "< 1m";
                                                      }
                                                    }
                                                    return (
                                                      <TableRow key={i}>
                                                        <TableCell
                                                          sx={{ fontSize: 12 }}
                                                        >
                                                          {emp.company_name}
                                                          {emp.is_current && (
                                                            <Chip
                                                              label="Current"
                                                              size="small"
                                                              sx={{
                                                                ml: 1,
                                                                fontSize: 9,
                                                                height: 16,
                                                                backgroundColor:
                                                                  "#e8f5e9",
                                                                color:
                                                                  "#2e7d32",
                                                              }}
                                                            />
                                                          )}
                                                        </TableCell>
                                                        <TableCell
                                                          sx={{ fontSize: 12 }}
                                                        >
                                                          {emp.job_title}
                                                        </TableCell>
                                                        <TableCell
                                                          sx={{ fontSize: 12 }}
                                                        >
                                                          {emp.employment_type}
                                                        </TableCell>
                                                        <TableCell
                                                          sx={{ fontSize: 12 }}
                                                        >
                                                          {start} – {end}
                                                        </TableCell>
                                                        <TableCell
                                                          sx={{ fontSize: 12 }}
                                                        >
                                                          {dur}
                                                        </TableCell>
                                                      </TableRow>
                                                    );
                                                  },
                                                )}
                                              </TableBody>
                                            </Table>
                                          </>
                                        )}
                                      </Box>
                                    </Collapse>
                                  </TableCell>
                                </TableRow>
                              </React.Fragment>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              );
            })()
          )}
        </>
      )}
    </Container>
  );
}
